/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import React, {
	ReactNode,
	useState,
	useEffect,
} from 'react';

// Style
import classes from './MenuFilter.module.css';

// Mantine
import {
	Popover,
	Checkbox,
	Box,
	TextInput,
	Stack,
	Button,
	Text,
	Image,
	ActionIcon,
	Tooltip,
} from '@mantine/core';
import {
	useClickOutside,
	useFocusTrap,
	useMediaQuery,
} from '@mantine/hooks';

import { Tooltip as CustomTooltip } from '@components/atoms/Tooltip';

// Helpers
import {
	capitalizeString,
	truncateString,
} from '@utils/functions';

// Icons
import SearchIcon from '@assets/icons/search.svg';
import ArrowDownIcon from '@assets/icons/arrow-down.svg';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

// Type
type Item = {
	label: string;
	value: any;
};

type MenuFilterProps = {
	onApply: (item: Item[]) => void;
	onChange?: (item: Item) => void;
	children?: ReactNode;
	size?: string;
	variant?: string;
	placeholder?: string;
	placeholderSearch?: string;
	color?: string;
	label?: string;
	options: Item[] | null;
	fullWidth?: boolean;
	loading?: boolean;
	disabled?: boolean;
	leftSection?: React.ReactNode;
	reset?: boolean;
	isPersist?: boolean;
	value?: any;
	opened?: boolean;
	handlereset?: (_isReset: boolean) => void;
	withoutSearch?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const MenuFilter = (props: MenuFilterProps) => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	/* ******************************** States ******************************* */
	const [selectedItems, setSelectedItems] = useState<
		Item[]
	>([]);
	const [appliedItems, setAppliedItems] = useState<
		Item[]
	>([]);

	const [searchTerm, setSearchTerm] =
		useState<string>('');
	const focusTrapRef = useFocusTrap();

	/* ******************************** RENDERING ******************************* */
	const { t } = useTranslation();
	/* ******************************** Variables ******************************* */
	const totalSelectedItems = selectedItems.length;
	const filteredOptions =
		props.options && Array.isArray(props.options)
			? props.options.filter((item: { label: string }) =>
					t(item.label)
						.toLowerCase()
						.includes(searchTerm.toLowerCase())
				)
			: [];
	/* ******************************** Helpers ******************************* */
	const handleRemoveItems = () => {
		setSelectedItems(appliedItems);
	};

	const refDropdown = useClickOutside(handleRemoveItems);

	const handleApply = () => {
		const updatedItems = [...selectedItems].map(
			(item) => ({ ...item })
		);
		setAppliedItems(updatedItems);
		setSelectedItems(updatedItems);
		props.onApply(selectedItems);
	};

	const handleCheckboxChange = (item: Item) => {
		const isSelected = selectedItems.some(
			(selectedItem) => selectedItem.value === item.value
		);

		if (isSelected) {
			setSelectedItems(
				selectedItems.filter(
					(selectedItem) =>
						selectedItem.value !== item.value
				)
			);
		} else {
			setSelectedItems([...selectedItems, item]);
		}
	};

	const handleClearSelectedItems = () => {
		setSelectedItems([]);
	};

	const handleChangeInputSearch = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setSearchTerm(event.target.value);
	};

	useEffect(() => {
		if (props && props.handlereset && props.reset) {
			setSelectedItems([]);
			setAppliedItems([]);
			props.handlereset(false);
		}
	}, [props.reset]);

	useEffect(() => {
		if (props.isPersist && props.value) {
			const objetsFiltres = props.options
				? props.options.filter((objet) =>
						props.value.includes(objet.value)
					)
				: [];
			setSelectedItems(objetsFiltres);
		}
	}, [props.isPersist]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Popover
			width={'target'}
			position="bottom"
			classNames={classes}
			clickOutsideEvents={['mouseup', 'touchend']}
			disabled={props.disabled}
			opened={props.opened}
			data-testid="MPFtG1R8_Z5jdHi6B-component"
		>
			<CustomTooltip
				label={
					selectedItems.length == 1 &&
					selectedItems[0].label.length > 19
						? t(selectedItems[0].label)
						: ''
				}
				isActive={
					selectedItems.length == 1 &&
					selectedItems[0].label.length > 19
				}
			>
				<Popover.Target>
					<Box
						size={props.size}
						className={clsx(
							!props.disabled
								? classes.containerTarget
								: classes.containerTargetDisabled,
							totalSelectedItems.toString() !== '0' &&
								classes.containerTargetSelected
						)}
						variant="outline"
					>
						{totalSelectedItems.toString() == '0'
							? props.placeholder
							: selectedItems.length > 1
								? capitalizeString(props.label) +
									' (' +
									totalSelectedItems.toString() +
									')'
								: truncateString(
										t(selectedItems[0].label),
										19
									)}

						<ActionIcon
							size={'12px'}
							variant="transparent"
							style={{
								marginLeft: '5px',
							}}
						>
							<Image
								className={clsx(
									classes.containerIconUp,
									props.disabled &&
										classes.containerIconUpDisabled
								)}
								src={ArrowDownIcon}
							/>
						</ActionIcon>
					</Box>
				</Popover.Target>
			</CustomTooltip>
			<Popover.Dropdown ref={refDropdown}>
				{!props.withoutSearch && (
					<TextInput
						ref={focusTrapRef}
						size={isBigScreen ? 'sm' : 'xs'}
						data-testid="D1CtGXwC_F5jdHi64-input"
						value={searchTerm}
						onChange={handleChangeInputSearch}
						leftSection={
							<Image
								className={classes.containerSearchIcon}
								src={SearchIcon}
							/>
						}
						placeholder={props.placeholderSearch}
						classNames={{
							input: classes.textSearch,
						}}
					/>
				)}

				<Box className={classes.containerOptions}>
					<Stack
						gap={'md'}
						style={{
							padding: '0px 5px',
						}}
					>
						{props &&
						filteredOptions &&
						Array.isArray(filteredOptions) &&
						filteredOptions.length > 0 ? (
							filteredOptions.map((item: any) => (
								<Box key={item.value}>
									<Tooltip
										label={t(item.label)}
										refProp={
											t(item.label).length > 20
												? 'rootRef'
												: ''
										}
									>
										<Checkbox
											data-testid={'xxl' + item.label}
											checked={selectedItems.some(
												(selectedItem) =>
													selectedItem.value ===
													item.value
											)}
											visibleFrom="xxl"
											size="sm"
											classNames={{
												label: classes.textOption,
											}}
											label={truncateString(
												t(item.label),
												20
											)}
											onChange={() => {
												handleCheckboxChange(item);
											}}
										/>
									</Tooltip>
									<Tooltip
										label={t(item.label)}
										refProp={
											t(item.label).length > 12
												? 'rootRef'
												: ''
										}
									>
										<Checkbox
											data-testid={item.label}
											checked={selectedItems.some(
												(selectedItem) =>
													selectedItem.value ===
													item.value
											)}
											onChange={() => {
												handleCheckboxChange(item);
											}}
											hiddenFrom="xxl"
											size="xs"
											classNames={{
												label: classes.textOption,
											}}
											label={truncateString(
												t(item.label),
												12
											)}
										/>
									</Tooltip>
								</Box>
							))
						) : (
							<Text className={classes.emptyItemsText}>
								{t('global.noData')}
							</Text>
						)}
					</Stack>
				</Box>
				<Box className={classes.containerButton}>
					<Button
						onClick={handleApply}
						visibleFrom="xxl"
						size="sm"
						fullWidth
					>
						{t('global.apply')}
					</Button>
					<Button
						onClick={handleApply}
						hiddenFrom="xxl"
						size="xs"
						fullWidth
						data-testid="M00kG1R1_Z5jdHi6B-button"
					>
						{t('global.apply')}
					</Button>
				</Box>
				<Box className={classes.containerSelected}>
					<Text className={classes.textSelectedNumber}>
						{t('global.selected')} (
						{totalSelectedItems
							? truncateString(
									totalSelectedItems.toString(),
									4
								)
							: 0}
						)
					</Text>
					<Text
						data-testid="D1CtGXwC_C5cdHi64-button"
						onClick={handleClearSelectedItems}
						className={classes.textClearSelected}
					>
						{t('global.clear')}
					</Text>
				</Box>
			</Popover.Dropdown>
		</Popover>
	);
};
